/**
 * Ground weapons (decision 118, BUILDLIST 65.1–65.3): a weapon's numbers for a person, read through the scale
 * profile, and its magazine. The rifle is today's gun — its damage, interval, spread, speed and life are the
 * person set's (so decision 24's tuning still reaches it); its magazine and reload come from GROUND_WEAPONS. The
 * pistol is GROUND_WEAPONS.pistol, its speed × k. Pure.
 */

import { explosionBlastFor } from './explosions';
import type { ExplosionClass } from '../constants/explosions';
import type { MountedGunKind, TankRound } from '../types/body';
import type { CartridgeKind, GameConfig, GroundWeaponKind, LongGunKind, PlayerState } from '../types';
import { GROUND_WEAPONS, AMMO_RESERVE, MOUNTED_GUNS, ACTION_WIND, TANK_GUN } from '../constants';
import { PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';
import { dragOf, muzzleDamage } from './ballistics';

export interface WeaponSpec {
  damage: number; interval: number; spread: number; speed: number; life: number; magazine: number; reload: number;
  /** 65.30: the round it fires (constants/ammo.ts) and its drag (core/ballistics.ts, per world unit); both absent for the rocket. `damage` is the muzzle's. */
  round?: CartridgeKind;
  drag?: number;
  /** S7: spare rounds a life (absent = unlimited), × the felt gravity its round falls by (absent = a bullet's), and its blast. */
  reserve?: number;
  gravity?: number;
  /** In world units at this scale: `radius` is the blast convention's (the damage circle is 2.2 × it, core/damage.ts). */
  blast?: { radius: number; damage: number };
  /** 65.31: the distance (world units at this scale) a round flies before it arms — a hit nearer is a dud (absent: armed at once). */
  armDistance?: number;
  /** 65.35: its burst's class (constants/explosions.ts), with `blast`. */
  explosion?: ExplosionClass;
  /** 65.46: its coast after its motor's burn (`life`): seconds, its share of the felt gravity, its drag (per world unit, core/ballistics.ts). */
  coast?: { seconds: number; gravity: number; drag: number };
  /** 65.43: fires on the press (`semi`, `bolt`; absent: held, automatic), and reloaded only when empty (the Garand's clip). */
  action?: 'semi' | 'bolt';
  reloadWhenEmpty?: true;
  /** 69.29: its penetration against a vehicle's zones (absent: its cartridge's), and a round that strikes a hull whole (the tank's solid). */
  penetration?: number;
  solid?: true;
}

/**
 * A weapon's numbers at this scale — 62.51: memoised per person set (its objects are cached and never change) and weapon
 * and the reserves switch, the only things it reads; the result is frozen and shared (every caller only reads it).
 */
const specMemo = new WeakMap<object, Map<string, WeaponSpec>>();
export function weaponSpec(ps: Parameters<typeof weaponSpecOf>[0], kind?: GroundWeaponKind, config?: Pick<GameConfig, 'ammoReserves'> & Partial<Pick<GameConfig, 'gl40Speed' | 'gl40Gravity'>>): WeaponSpec {
  let m = specMemo.get(ps);
  if (!m) { m = new Map(); specMemo.set(ps, m); }
  const key = `${kind ?? 'rifle'}|${config?.ammoReserves ? 1 : 0}${kind === 'gl40' ? `|${config?.gl40Speed ?? ''}|${config?.gl40Gravity ?? ''}` : ''}`;   // (65.31: the 40 mm's Lab dials)
  let w = m.get(key);
  if (!w) { w = Object.freeze(weaponSpecOf(ps, kind, config)); m.set(key, w); }
  return w;
}

function weaponSpecOf(ps: Pick<PersonSet, 'bulletDamage' | 'fireInterval' | 'bulletSpread' | 'bulletSpeed' | 'bulletLife' | 'k'> & Partial<Pick<PersonSet, 'bulletSpeedScale'>>, kind: GroundWeaponKind | undefined, config?: Pick<GameConfig, 'ammoReserves'> & Partial<Pick<GameConfig, 'gl40Speed' | 'gl40Gravity'>>): WeaponSpec {
  // 65.10: with ammo reserves on, the rifle and the pistol keep limited spare rounds too (absent = unlimited, as before)
  const reserve = config?.ammoReserves && kind !== 'rocket' && kind !== 'gl40' && kind !== 'panzerfaust' && kind !== 'missile' ? AMMO_RESERVE[kind ?? 'rifle'] : undefined;   // (65.43: the Panzerfaust is thrown away, no spare)
  if (!kind || kind === 'rifle') {
    const r = GROUND_WEAPONS.rifle;
    const round = r.round!;
    return { round, drag: dragOf(round, PERSON_BODY_HEIGHT * ps.k), damage: muzzleDamage(round, ps.bulletDamage), interval: ps.fireInterval, spread: ps.bulletSpread, speed: ps.bulletSpeed, life: ps.bulletLife, magazine: r.magazine, reload: r.reload, ...(reserve !== undefined ? { reserve } : {}) };
  }
  if (GROUND_WEAPONS[kind].round !== undefined && reserve !== undefined) {   // (a bullet gun but the rifle: the pistol, the SMG, the LMG, 65.43's classic guns)
    const { explosion: _b, carry: _c, coast: _k, ...w } = GROUND_WEAPONS[kind]; return { ...w, ...ofRound(w, ps.k), speed: w.speed * ps.k * (ps.bulletSpeedScale ?? 1), reserve }; }
  const { explosion, armPeople, coast, ...w } = GROUND_WEAPONS[kind];
  const blast = explosion ? explosionBlastFor(ps, explosion) : null;   // 65.35: its class's burst at this scale
  return {
    ...w, ...ofRound(w, ps.k), speed: w.speed * ps.k * (kind === 'rocket' || kind === 'panzerfaust' ? 1 : ps.bulletSpeedScale ?? 1),   // 67.13: bullets take the speed tuning; the rocket flies on its motor (65.43: the Panzerfaust's round is no bullet)
    // S7: a person-family blast: its damage circle `radiusPeople` person heights (SCALE.md §6), so the convention's radius ÷ 2.2 (65.35: its class's, core/explosions.ts)
    ...(blast ? { blast, explosion } : {}),
    ...(armPeople ? { armDistance: armPeople * PERSON_BODY_HEIGHT * ps.k } : {}),   // 65.31: the 40 mm's arming distance
    ...(coast ? { coast: { seconds: coast.seconds, gravity: coast.gravity, drag: Math.LN2 / (coast.halfSpeedPeople * PERSON_BODY_HEIGHT * ps.k) } } : {}),   // 65.46: the rocket's coast (its drag as a round's, ballistics.ts dragOf)
    // 65.31 (the owner: tune its lob in play): the Lab's 40 mm speed (× its muzzle speed) and gravity (its share of the felt gravity)
    ...(kind === 'gl40' && config?.gl40Speed !== undefined ? { speed: w.speed * ps.k * config.gl40Speed } : {}),
    ...(kind === 'gl40' && config?.gl40Gravity !== undefined ? { gravity: config.gl40Gravity } : {}),
  };
}

/** 65.30: a bullet weapon's muzzle damage (its round's damage at the reference distance, constants/ammo.ts) and drag. */
function ofRound(w: { round?: CartridgeKind; damage: number }, k: number): { damage: number; drag?: number } {
  return w.round ? { damage: muzzleDamage(w.round, w.damage), drag: dragOf(w.round, PERSON_BODY_HEIGHT * k) } : { damage: w.damage };
}

/**
 * 63.1d step 3 / 65.22: a mounted bullet gun's numbers at this scale (MOUNTED_GUNS, the rifle's round × its ratios): its
 * round's speed takes the scale and the bullets' speed tuning as every bullet's; `magazine` is its belt.
 */
export function mountedGunSpec(ps: Pick<PersonSet, 'k'> & Partial<Pick<PersonSet, 'bulletSpeedScale'>>, kind: MountedGunKind): WeaponSpec {
  const g = MOUNTED_GUNS[kind];
  return { round: g.round, ...ofRound(g, ps.k), interval: g.interval, spread: g.spread, speed: g.speed * ps.k * (ps.bulletSpeedScale ?? 1), life: g.life, magazine: g.belt, reload: g.reload };
}

/**
 * 69.29: the tank's main gun's round at this scale (TANK_GUN): the explosive one bursts as `tankHe` where it stops (armed past
 * `armPeople`), the solid one strikes whole (its `damage` and `penetration`). One round in the gun; `reload` loads the next.
 */
export function tankRoundSpec(ps: Pick<PersonSet, 'k'> & Parameters<typeof explosionBlastFor>[0], round: TankRound): WeaponSpec {
  const g = TANK_GUN[round], base = { interval: TANK_GUN.reload, magazine: 1, reload: TANK_GUN.reload, spread: g.spread, speed: g.speed * ps.k, life: g.life, gravity: g.gravity };
  if (round === 'he') return { ...base, damage: 0, blast: explosionBlastFor(ps, 'tankHe')!, explosion: 'tankHe', armDistance: TANK_GUN.he.armPeople * PERSON_BODY_HEIGHT * ps.k };
  return { ...base, damage: TANK_GUN.solid.damage, penetration: TANK_GUN.solid.penetration, solid: true };
}

/** S7: spare rounds left for a weapon with a limited supply (Infinity when unlimited). */
export function reserveLeft(p: Pick<PlayerState, 'reserves'>, kind: GroundWeaponKind, spec: Pick<WeaponSpec, 'reserve'>): number {
  return spec.reserve === undefined ? Infinity : p.reserves?.[kind] ?? spec.reserve;
}

/** 65.13 / 65.14: a person's primary long gun (absent = the rifle). */
export function primaryOf(p: Pick<PlayerState, 'primary'>): LongGunKind {
  return p.primary ?? 'rifle';
}

/** 65.43: a person's sidearm (absent = the pistol; the M1911 in its place). */
export function sidearmOf(p: Partial<Pick<PlayerState, 'sidearm'>>): 'pistol' | 'm1911' {
  return p.sidearm ?? 'pistol';
}

/** The weapon in a person's hands (absent = their primary: the rifle unless their kit says otherwise). */
export function weaponOf(p: Pick<PlayerState, 'weapon'> & Partial<Pick<PlayerState, 'primary'>>): GroundWeaponKind {
  return p.weapon ?? primaryOf(p);
}

/** Rounds left in the magazine of the weapon in hand (absent = full). */
export function roundsLeft(p: Pick<PlayerState, 'weapon' | 'magazines'> & Partial<Pick<PlayerState, 'primary'>>, spec: Pick<WeaponSpec, 'magazine'>): number {
  return p.magazines?.[weaponOf(p)] ?? spec.magazine;
}

/** Rounds left in one weapon's magazine, in hand or not (absent = full). */
export function roundsIn(p: Pick<PlayerState, 'magazines'>, kind: GroundWeaponKind, spec: Pick<WeaponSpec, 'magazine'>): number {
  return p.magazines?.[kind] ?? spec.magazine;
}

/**
 * 65.4: nothing to shoot with — the weapon in hand is empty or reloading, and the other one is empty too (or the
 * unit is still drawing it). A bot under fire in this state goes to cover to reload.
 */
export function outOfRounds(p: Pick<PlayerState, 'weapon' | 'magazines' | 'reloadLeft'> & Partial<Pick<PlayerState, 'primary' | 'sidearm'>>, ps: Parameters<typeof weaponSpec>[0]): boolean {
  const inHand = weaponOf(p), other: GroundWeaponKind = inHand === primaryOf(p) ? sidearmOf(p) : primaryOf(p);
  const dry = p.reloadLeft !== undefined || roundsIn(p, inHand, weaponSpec(ps, inHand)) <= 0;
  return dry && roundsIn(p, other, weaponSpec(ps, other)) <= 0;
}

/**
 * 65.10: a reload's end — the magazine takes what it is missing from the spares (all of it when the spares are
 * unlimited); a limited supply short of a full magazine loads what it has. The rocket (one round, one spare) is as
 * before. Returns the new magazines and reserves.
 */
export function finishReload(p: Pick<PlayerState, 'weapon' | 'magazines' | 'reserves'>, spec: Pick<WeaponSpec, 'magazine' | 'reserve'>): Pick<PlayerState, 'magazines' | 'reserves'> {
  const kind = weaponOf(p), have = roundsLeft(p, spec), spare = reserveLeft(p, kind, spec);
  const loaded = Math.min(spec.magazine, have + spare);
  const mags = { ...(p.magazines ?? {}) };
  if (loaded >= spec.magazine) delete mags[kind]; else mags[kind] = loaded;
  return {
    magazines: Object.keys(mags).length ? mags : undefined,
    reserves: spec.reserve === undefined ? p.reserves : { ...(p.reserves ?? {}), [kind]: spare - (loaded - have) },
  };
}

/**
 * 65.13 / 65.14 (owner: "a 50%rifle:50%smg against all rifle"): a team's long guns written as text — one kind (`smg`),
 * or shares (`rifle:1,smg:1`, `rifle:3,lmg:1`; a kind alone counts 1) — read to shares; null for nothing readable.
 * Used by the sims' `--guns` and the Lab's per-side rows.
 */
export function parseGunShares(text: string | undefined | null): Partial<Record<LongGunKind, number>> | null {
  if (!text) return null;
  const out: Partial<Record<LongGunKind, number>> = {};
  for (const part of text.split(',').map(x => x.trim()).filter(Boolean)) {
    const [k, v] = part.split(':').map(x => x.trim());
    if (k !== 'rifle' && k !== 'smg' && k !== 'lmg') continue;
    const n = v === undefined ? 1 : Number(v);
    if (Number.isFinite(n) && n > 0) out[k] = (out[k] ?? 0) + n;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * 65.31: the wind's share for a round (core/wind.ts actionWindAccel's scale) — one rule for every reader (the flight, the
 * bots' wind solve, the assist and the impact dot): a motor weathervanes (the rocket), a lob takes the 40 mm's own share (the
 * Lab's 40 mm wind, else ACTION_WIND.gl40), a bullet a bullet's.
 */
export function windShare(round: { blast?: unknown; gravity?: number; wind?: number }, config?: Partial<Pick<GameConfig, 'gl40Wind'>>): number {
  if (round.wind !== undefined) return round.wind;   // (69.64: a round with its own — a gun's shell)
  if (!round.blast) return ACTION_WIND.bullet;
  if (round.gravity === 0) return ACTION_WIND.rocket;
  return config?.gl40Wind ?? ACTION_WIND.gl40;
}

/**
 * 65.43: each weapon's kin — the one it draws and sounds as until its own model and voice land (lane E reads the keys):
 * the Garand and the Springfield the rifle, the Thompson the SMG, the BAR the LMG, the M1911 the pistol, the Panzerfaust
 * the LAW; today's guns their own.
 */
export type WeaponKin = 'rifle' | 'pistol' | 'rocket' | 'smg' | 'lmg' | 'gl40';
export const WEAPON_KIN: Record<GroundWeaponKind, WeaponKin> = {
  rifle: 'rifle', pistol: 'pistol', rocket: 'rocket', smg: 'smg', lmg: 'lmg', gl40: 'gl40',
  garand: 'rifle', springfield: 'rifle', m1911: 'pistol', thompson: 'smg', bar: 'lmg', panzerfaust: 'rocket', missile: 'rocket',   // (65.49: a tube in the LAW's place)
};
export function weaponKin(kind: GroundWeaponKind): WeaponKin { return WEAPON_KIN[kind]; }
/** 65.43: a long gun's kin — the rifle, the SMG or the LMG. */
export function longGunKin(kind: LongGunKind): 'rifle' | 'smg' | 'lmg' { return WEAPON_KIN[kind] as 'rifle' | 'smg' | 'lmg'; }
